// =============================================================================
// Nexus UI — vanilla TS, dependency-injected `fetch`.
// =============================================================================

export type Nexus = {
  id: string;
  praesyn: string;
  postsyn: string;
  associatio: string;
  tractus: string | null;
  pondus: number | null;
  directus: number;
  nota: string | null;
  created_at: number;
  updated_at: number;
};

export type FetchLike = typeof fetch;

export type UiDeps = {
  fetch: FetchLike;
  baseUrl: string;
  token: string;
};

export type UiResult<T> = {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
};

export type BrowseFilter = {
  praesyn?: string;
  postsyn?: string;
  associatio?: string;
  tractus?: string;
  directus?: number;
  limit?: number;
};

export function makeUi(deps: UiDeps) {
  async function call<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<UiResult<T>> {
    let res: Response;
    try {
      res = await deps.fetch(`${deps.baseUrl.replace(/\/$/, "")}${path}`, {
        method,
        headers: {
          ...(deps.token ? { authorization: `Bearer ${deps.token}` } : {}),
          ...(body !== undefined ? { "content-type": "application/json" } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      return { ok: false, status: 0, data: null, error: String(err) };
    }
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text;
    }
    if (!res.ok) {
      const msg =
        parsed && typeof parsed === "object" && "error" in parsed
          ? JSON.stringify((parsed as { error: unknown }).error)
          : String(parsed ?? res.statusText);
      return { ok: false, status: res.status, data: null, error: msg };
    }
    return { ok: true, status: res.status, data: parsed as T, error: null };
  }

  function qs(f: BrowseFilter & { count?: boolean }): string {
    const p = new URLSearchParams();
    if (f.praesyn) p.set("praesyn", f.praesyn);
    if (f.postsyn) p.set("postsyn", f.postsyn);
    if (f.associatio) p.set("associatio", f.associatio);
    if (f.tractus) p.set("tractus", f.tractus);
    if (f.directus != null) p.set("directus", String(f.directus));
    if (f.limit != null) p.set("limit", String(f.limit));
    if (f.count) p.set("$count", "true");
    const s = p.toString();
    return s ? `?${s}` : "";
  }

  return {
    browse(filter: BrowseFilter = {}) {
      return call<{ value: Nexus[]; "@odata.count"?: number }>(
        "GET",
        `/Nexus${qs({ ...filter, count: true })}`,
      );
    },
    findByAssociatio(query: string, mode: "prefix" | "fts" | "exact" = "prefix") {
      return call<{ value: Nexus[] }>("POST", `/Nexus/findByAssociatio`, {
        query,
        mode,
        limit: 100,
      });
    },
    vicini(entity_id: string, opts?: { associatio?: string; directed_only?: boolean }) {
      return call<{ value: Nexus[] }>("POST", `/Nexus/vicini`, {
        entity_id,
        ...opts,
        limit: 100,
      });
    },
    read(id: string) {
      return call<Nexus>("GET", `/Nexus('${encodeURIComponent(id)}')`);
    },
    create(body: Partial<Nexus>) {
      return call<Nexus>("POST", `/Nexus`, body);
    },
    update(id: string, patch: { associatio?: string; tractus?: string | null; pondus?: number | null; nota?: string | null }) {
      return call<Nexus>("PATCH", `/Nexus('${encodeURIComponent(id)}')`, patch);
    },
    remove(id: string) {
      return call<null>("DELETE", `/Nexus('${encodeURIComponent(id)}')`);
    },
    ping() {
      return call<{ service: string; version: string }>("GET", ``);
    },
  };
}

export type Ui = ReturnType<typeof makeUi>;

// -----------------------------------------------------------------------------
// Settings (localStorage) — browser only.
// -----------------------------------------------------------------------------

export type Settings = { baseUrl: string; token: string };
const LS_KEY = "thw8-nexus-ui:settings";

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as Settings;
  } catch {
    /* ignore */
  }
  return { baseUrl: "http://localhost:8787/v0.1.0", token: "" };
}

export function saveSettings(s: Settings): void {
  localStorage.setItem(LS_KEY, JSON.stringify(s));
}

// -----------------------------------------------------------------------------
// Browser attach — only runs in a DOM (skipped in headless tests).
// -----------------------------------------------------------------------------

if (typeof document !== "undefined") {
  let settings = loadSettings();
  let ui = makeUi({
    fetch: globalThis.fetch.bind(globalThis),
    baseUrl: settings.baseUrl,
    token: settings.token,
  });

  const $ = <T extends HTMLElement>(sel: string): T => {
    const el = document.querySelector(sel);
    if (!el) throw new Error(`element not found: ${sel}`);
    return el as T;
  };
  const setStatus = (msg: string, kind: "info" | "error" = "info") => {
    const s = $("#status");
    s.textContent = msg;
    s.classList.toggle("error", kind === "error");
  };
  const fmtDate = (ms: number | null | undefined) =>
    ms ? new Date(ms).toISOString().slice(0, 19).replace("T", " ") : "—";

  function rebuildUi() {
    ui = makeUi({
      fetch: globalThis.fetch.bind(globalThis),
      baseUrl: settings.baseUrl,
      token: settings.token,
    });
  }

  function renderRows(rows: Nexus[]) {
    const tbody = $("#results-table tbody");
    tbody.innerHTML = "";
    if (!rows.length) {
      tbody.innerHTML = `<tr><td colspan="9" class="muted">— no results —</td></tr>`;
      return;
    }
    for (const row of rows) {
      const tr = document.createElement("tr");
      tr.dataset.id = row.id;
      tr.innerHTML = `
        <td class="id">${row.id}</td>
        <td>${row.praesyn}</td>
        <td>${row.postsyn}</td>
        <td>${row.associatio}</td>
        <td>${row.tractus ?? "—"}</td>
        <td>${row.pondus ?? "—"}</td>
        <td>${row.directus === 1 ? "→" : "↔"}</td>
        <td class="muted">${fmtDate(row.created_at)}</td>
        <td><button class="row-open" type="button">↗</button></td>`;
      tbody.appendChild(tr);
    }
  }

  function showDetail(row: Nexus) {
    const d = $<HTMLElement>("#detail");
    d.hidden = false;
    $("#detail-content").textContent = JSON.stringify(row, null, 2);
    d.dataset.id = row.id;
    d.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
  const hideDetail = () => ($<HTMLElement>("#detail").hidden = true);

  async function doBrowse() {
    setStatus("loading…");
    const r = await ui.browse({
      praesyn: $<HTMLInputElement>("#filter-praesyn").value || undefined,
      postsyn: $<HTMLInputElement>("#filter-postsyn").value || undefined,
      associatio: $<HTMLInputElement>("#filter-associatio").value || undefined,
      tractus: $<HTMLInputElement>("#filter-tractus").value || undefined,
      limit: 100,
    });
    if (!r.ok || !r.data) return setStatus(r.error ?? "error", "error");
    renderRows(r.data.value);
    setStatus(`${r.data.value.length} rows (total: ${r.data["@odata.count"] ?? "?"})`);
  }

  async function doSearch() {
    const query = $<HTMLInputElement>("#search-query").value.trim();
    if (!query) return doBrowse();
    const mode = $<HTMLSelectElement>("#search-mode").value as "prefix" | "fts" | "exact";
    setStatus(`searching (${mode})…`);
    const r = await ui.findByAssociatio(query, mode);
    if (!r.ok || !r.data) return setStatus(r.error ?? "error", "error");
    renderRows(r.data.value);
    setStatus(`${r.data.value.length} results (${mode})`);
  }

  async function doCreate(ev: SubmitEvent) {
    ev.preventDefault();
    const form = ev.target as HTMLFormElement;
    const fd = new FormData(form);
    const body: Record<string, unknown> = {};
    const praesyn = String(fd.get("praesyn") ?? "").trim();
    const postsyn = String(fd.get("postsyn") ?? "").trim();
    const associatio = String(fd.get("associatio") ?? "").trim();
    const tractus = String(fd.get("tractus") ?? "").trim();
    const pondus = String(fd.get("pondus") ?? "").trim();
    const nota = String(fd.get("nota") ?? "").trim();
    if (praesyn) body.praesyn = praesyn;
    if (postsyn) body.postsyn = postsyn;
    if (associatio) body.associatio = associatio;
    if (tractus) body.tractus = tractus;
    if (pondus) body.pondus = Number(pondus);
    if (nota) body.nota = nota;
    setStatus("creating…");
    const r = await ui.create(body as never);
    if (!r.ok || !r.data) return setStatus(r.error ?? "error", "error");
    setStatus(`created: ${r.data.id}`);
    form.reset();
    await doBrowse();
    showDetail(r.data);
  }

  async function doDelete() {
    const id = $<HTMLElement>("#detail").dataset.id;
    if (!id || !confirm(`Delete: ${id}?`)) return;
    const r = await ui.remove(id);
    if (!r.ok) return setStatus(r.error ?? "error", "error");
    setStatus(`deleted: ${id}`);
    hideDetail();
    await doBrowse();
  }

  async function openRow(id: string) {
    setStatus("loading…");
    const r = await ui.read(id);
    if (!r.ok || !r.data) return setStatus(r.error ?? "error", "error");
    showDetail(r.data);
    setStatus("");
  }

  // Settings dialog
  const dlg = $<HTMLDialogElement>("#settings-dialog");
  $("#settings-btn").addEventListener("click", () => {
    $<HTMLInputElement>("#settings-url").value = settings.baseUrl;
    $<HTMLInputElement>("#settings-token").value = settings.token;
    dlg.showModal();
  });
  $("#settings-cancel").addEventListener("click", () => dlg.close());
  dlg.addEventListener("close", async () => {
    if (dlg.returnValue !== "save") return;
    settings = {
      baseUrl: $<HTMLInputElement>("#settings-url").value.trim(),
      token: $<HTMLInputElement>("#settings-token").value,
    };
    saveSettings(settings);
    rebuildUi();
    const p = await ui.ping();
    setStatus(p.ok ? "backend OK" : `backend unavailable: ${p.error}`, p.ok ? "info" : "error");
    if (p.ok) await doBrowse();
  });

  $("#search-btn").addEventListener("click", () => void doSearch());
  $("#browse-btn").addEventListener("click", () => void doBrowse());
  $("#search-query").addEventListener("keydown", (ev) => {
    if ((ev as KeyboardEvent).key === "Enter") void doSearch();
  });
  $("#create-form").addEventListener("submit", (ev) => void doCreate(ev as SubmitEvent));
  $("#detail-close").addEventListener("click", hideDetail);
  $("#detail-delete").addEventListener("click", () => void doDelete());
  $("#results-table").addEventListener("click", (ev) => {
    const tr = (ev.target as HTMLElement).closest("tr[data-id]") as HTMLTableRowElement | null;
    if (tr?.dataset.id) void openRow(tr.dataset.id);
  });

  if (settings.token && settings.baseUrl) void doBrowse();
  else setStatus("⚙️ Configure the settings (top right)");
}
